import { InformationRowContainer } from "@/components/lazo-one-settings-components/profile-components/CompanyInformation";
import CommonButton from "@/components/utils/CommonButton";
import CommonFormControl from "@/components/utils/CommonFormControl";
import LazoModal from "@/components/utils/LazoModal";
import { CommonStyledSelect, CommonStyledSelectMenuProps } from "@/components/utils/SelectStyles";
import { Colors } from "@/enums/Colors";
import { getJobTitleTranslation } from "@/enums/UserJobTitle";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { useResponsive } from "@/hooks/useResponsive";
import { LazoUser } from "@/models/LazoUser";
import LazoUserService from "@/services/LazoUserService";
import OnboardingService from "@/services/OnboardingService";
import { LazoJobTitle } from "@/types/LazoJobTitle";
import { ApiResponseErrorUtils } from "@/utils/ApiResponseErrorUtils";
import { ExpandMore } from "@mui/icons-material";
import { FormLabel, TextField } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import { Box, styled } from "@mui/system";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

export const InformationFormContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  [theme.breakpoints.up("sm")]: {
    maxHeight: "65vh",
    padding: "1rem 0",
  },
}));

type PersonalInformationEditFormProps = {
  lazoUser: LazoUser;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setLazoUser: Dispatch<SetStateAction<LazoUser>>;
};

function PersonalInformationEditForm(props: PersonalInformationEditFormProps) {
  const { t } = useTranslation(["settings", "common"]);
  const { isMobile } = useResponsive();
  const [loading, setLoading] = useState<boolean>(false);
  const [externalJobTitles, setExternalJobTitles] = useState<LazoJobTitle[]>([]);

  useEffect(() => {
    OnboardingService.getExternalJobTitles().then((r) => {
      r.onSuccess((data) => setExternalJobTitles(data));
    });
  }, []);

  const jobTitleOptions = useMemo(() => {
    return [...externalJobTitles.map((jt) => jt.name), "Other"].sort();
  }, [externalJobTitles]);

  const { register, handleSubmit, control } = useForm();

  function onSubmit(data) {
    setLoading(true);
    LazoUserService.update({
      externalId: props.lazoUser.externalId,
      user: {
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
        email: data.email,
      },
      ...data,
    })
      .then((r) => {
        r.onSuccess((updated) => {
          props.setLazoUser(updated);
          NotificationService.info(t("profile.personalInformation.editModal.notifications.updateSuccess"));
        });
        r.onError((_, response) => ApiResponseErrorUtils.handleStatusCode(response));
      })
      .finally(() => {
        setLoading(false);
        props.setOpen(false);
      });
  }

  return (
    <form>
      <LazoModal
        title={t("profile.personalInformation.editModal.title")}
        open={props.open}
        handleClose={() => props.setOpen(false)}
        sx={{ width: { xs: "90vw", sm: "35.313rem" }, maxWidth: { xs: "500px", sm: "35.313rem" } }}
        buttons={
          loading
            ? [
                <Box sx={{ width: "100%" }} key={"spinner"}>
                  <Spinner sx={{ color: Colors.LAZO_PURPLE }} variant={"indeterminate"} />
                </Box>,
              ]
            : [
                <CommonButton
                  className={"purple-border-variant"}
                  style={{ borderRadius: "0.5rem" }}
                  onClick={() => props.setOpen(false)}
                  key={"cancel"}
                >
                  {t("common:cancel")}
                </CommonButton>,
                <CommonButton style={{ borderRadius: "0.5rem" }} onClick={handleSubmit(onSubmit)} type={"submit"} key={"save"}>
                  {t("common:save")}
                </CommonButton>,
              ]
        }
      >
        <InformationFormContainer>
          <InformationRowContainer gap={"inherit"}>
            <CommonFormControl sx={{ width: { xs: "100%", sm: "50%" } }}>
              <FormLabel>{t("profile.personalInformation.firstName")}</FormLabel>
              <Controller
                control={control}
                name={"firstName"}
                defaultValue={props.lazoUser.user.firstName}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <TextField
                    {...register("firstName", { required: true })}
                    defaultValue={value}
                    placeholder={error && t("profile.personalInformation.editModal.validation.firstNameRequired")}
                    onChange={onChange}
                    error={!!error}
                    value={value}
                  />
                )}
              />
            </CommonFormControl>
            <CommonFormControl sx={{ width: { xs: "100%", sm: "50%" } }}>
              <FormLabel>{t("profile.personalInformation.lastName")}</FormLabel>
              <Controller
                control={control}
                name={"lastName"}
                defaultValue={props.lazoUser.user.lastName}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <TextField
                    {...register("lastName", { required: true })}
                    defaultValue={value}
                    placeholder={error && t("profile.personalInformation.editModal.validation.lastNameRequired")}
                    onChange={onChange}
                    error={!!error}
                    value={value}
                  />
                )}
              />
            </CommonFormControl>
          </InformationRowContainer>
          <InformationRowContainer gap={"inherit"}>
            <CommonFormControl sx={{ width: { xs: "100%", sm: "50%" } }}>
              <FormLabel>{t("profile.personalInformation.email")}</FormLabel>
              <Controller
                control={control}
                name={"email"}
                defaultValue={props.lazoUser.user.email}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <TextField
                    {...register("email", { required: true })}
                    onChange={onChange}
                    error={!!error}
                    value={value}
                    placeholder={error && t("profile.personalInformation.editModal.validation.emailRequired")}
                    defaultValue={props.lazoUser.user.email}
                  />
                )}
              />
            </CommonFormControl>
            <CommonFormControl sx={{ width: { xs: "100%", sm: "50%" } }}>
              <FormLabel>{t("profile.personalInformation.telephone")}</FormLabel>
              <Controller
                control={control}
                name={"phone"}
                defaultValue={props.lazoUser.user.phone}
                render={({ field: { onChange, value } }) => (
                  <TextField {...register("phone")} onChange={onChange} value={value} defaultValue={props.lazoUser.user.phone} />
                )}
              />
            </CommonFormControl>
          </InformationRowContainer>
          {props.lazoUser.title && (
            <CommonFormControl>
              <FormLabel>{t("profile.personalInformation.jobTitle")}</FormLabel>
              <Controller
                control={control}
                name={"title"}
                defaultValue={props.lazoUser.title}
                render={({ field: { value } }) => (
                  <CommonStyledSelect
                    {...register("title")}
                    IconComponent={ExpandMore}
                    value={value}
                    //defaultValue={props.lazoUser.title}
                    MenuProps={CommonStyledSelectMenuProps}
                  >
                    {jobTitleOptions.map((title) => (
                      <MenuItem key={title} value={title}>
                        {getJobTitleTranslation(t, title)}
                      </MenuItem>
                    ))}
                  </CommonStyledSelect>
                )}
              />
            </CommonFormControl>
          )}
          <InformationRowContainer gap={"inherit"}>
            <CommonFormControl sx={{ width: { xs: "100%", sm: "33.33%" } }}>
              <FormLabel>{t("profile.personalInformation.instagram")}</FormLabel>
              <Controller
                control={control}
                name={"instagram"}
                defaultValue={props.lazoUser.instagram}
                render={({ field: { onChange, value } }) => (
                  <TextField {...register("instagram")} onChange={onChange} value={value} defaultValue={props.lazoUser.instagram} />
                )}
              />
            </CommonFormControl>
            <CommonFormControl sx={{ width: { xs: "100%", sm: "33.33%" } }}>
              <FormLabel>{t("profile.personalInformation.linkedin")}</FormLabel>
              <Controller
                control={control}
                name={"linkedIn"}
                defaultValue={props.lazoUser.linkedIn}
                render={({ field: { onChange, value } }) => (
                  <TextField {...register("linkedIn")} onChange={onChange} value={value} defaultValue={props.lazoUser.linkedIn} />
                )}
              />
            </CommonFormControl>
            <CommonFormControl sx={{ width: { xs: "100%", sm: "33.33%" } }}>
              <FormLabel>{t("profile.personalInformation.twitter")}</FormLabel>
              <Controller
                control={control}
                name={"twitter"}
                defaultValue={props.lazoUser.twitter}
                render={({ field: { onChange, value } }) => (
                  <TextField {...register("twitter")} onChange={onChange} value={value} defaultValue={props.lazoUser.twitter} />
                )}
              />
            </CommonFormControl>
          </InformationRowContainer>
          <CommonFormControl>
            <FormLabel>{t("profile.personalInformation.location")}</FormLabel>
            <Controller
              control={control}
              name={"location"}
              defaultValue={props.lazoUser.location}
              render={({ field: { onChange, value } }) => (
                <TextField {...register("location")} onChange={onChange} value={value} defaultValue={props.lazoUser.location} />
              )}
            />
          </CommonFormControl>
          <CommonFormControl>
            <FormLabel>{t("profile.personalInformation.bio")}</FormLabel>
            <Controller
              control={control}
              name={"bio"}
              defaultValue={props.lazoUser.bio}
              render={({ field: { onChange, value } }) => (
                <TextField {...register("bio")} onChange={onChange} value={value} defaultValue={props.lazoUser.bio} />
              )}
            />
          </CommonFormControl>
        </InformationFormContainer>
      </LazoModal>
    </form>
  );
}

export default PersonalInformationEditForm;
